/**
 * Brand step artwork (`src/assets/*.svg`) — the three "how it works" illustrations, inlined so they
 * take the theme instead of loading as flat <img> files.
 *
 * Each is a COMPLETE badge: a full-bleed disc carrying the art, unlike `ui/icons.tsx`, whose icons are
 * bare `currentColor` strokes that sit inside a CSS-drawn circle. So they render through `.step-art`
 * (which draws no disc of its own), not the `.fp-step-n` / `.intro-step-ic` badge.
 *
 * The art is two stacked layers on that disc: solid white shapes, then the original line work on top in
 * near-black (#2B2B2B). Both are literal — only the disc is `currentColor` (the source hardcoded
 * #3F3F3F, which would have stayed grey on the blue/rose/dark themes), so `.step-art` colours it from
 * `--accent` and every brand gets its own. Restoring the literal grey is one `color:` declaration in
 * base.css.
 *
 * Dropped from the export: Illustrator's element ids, its empty 24x24 bounding rects, and the wrapper
 * groups that held them — the two `translate(3 3) scale(0.75)` groups below are the source's own.
 *
 * A second, lighter set (`src/assets/*-line.svg`, chosen with `tradeInConfig.stepIcons.style: "line"`)
 * is fine-line drawing with no disc at all, stroked in `currentColor` — so `.step-art` gives it the
 * accent too (the sources are drawn in #484845, GAI+LISVA's grey, the brand it was made for). Its
 * viewBox is cropped to the drawing so it fills the same badge box as the discs do.
 *
 * A third set (`src/assets/*-classic.svg`, `stepIcons.style: "classic"`) is v1's own step icons, copied
 * from `packages/v1/src/svg/SvgIcons.tsx` so a brand moving from v1 keeps them once v1 is retired.
 * Filled glyphs with no disc, in `currentColor` (v1 used its contrast panel's text colour); same
 * clean-up as the badges, and the same cropped viewBox as the line set.
 */
import type { SVGProps } from 'react'
import { getStepIcons, getStepIconStyle, StepIconStyle } from '../providers/embedConfig'
import { cssLength } from '../util/cssLength'

type IconProps = SVGProps<SVGSVGElement>

export const ArtSelectStyles = (p: IconProps) => (
  <svg viewBox="0 0 24 24" {...p}>
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <g transform="translate(3 3) scale(0.75)" fill="#fff">
      <path d="M 14.841146 0.20052083 C 14.8085 0.2027019 14.776335 0.21035765 14.744792 0.21875
            C 14.584727 0.1782837 14.415004 0.2184448 14.291667 0.328125
            C 14.181972 0.3928223 14.097336 0.5001221 14.0625 0.6328125
            C 14.0615 0.6366575 14.060796 0.63938357 14.059896 0.64322917
            C 13.729558 1.1048685 13.047222 1.8552246 12.307292 1.9375
            C 11.768184 1.8850708 11.219065 1.447347 10.690104 0.63802083
            C 10.569865 0.45375573 10.347509 0.37511193 10.145833 0.42708333
            C 10.014058 0.39269003 9.8702443 0.4116821 9.7473958 0.4921875
            C 9.7332967 0.5014038 9.7211812 0.5131225 9.7083333 0.5234375 L 8.4557292 0.7578125
            C 8.4418132 0.7602535 8.4302167 0.761719 8.4166667 0.765625 L 6.828125 1.2057292
            C 6.77002 1.2218422 6.7153727 1.2484542 6.6666667 1.2838542 L 4.9557292 2.5286458
            C 4.9470592 2.5349958 4.9404707 2.5426432 4.9322917 2.5494792 L 3.3177083 3.890625
            C 3.1286215 4.0478515 3.0826009 4.3198242 3.2083333 4.53125 L 4.2682292 6.3151042
            L 5.3203125 8.0390625 C 5.3980715 8.1662598 5.5295003 8.2524414 5.6770833 8.2734375
            C 5.8252767 8.2949215 5.9729818 8.2490236 6.0833333 8.1484375 L 6.609375 7.6692708
            L 6.6953125 9.0885417 L 6.5572917 11.567708
            C 6.5560717 11.590898 6.5553385 11.614831 6.5572917 11.638021 L 6.7291667 13.692708
            L 6.7265625 13.692708 L 6 13.664062
            C 5.8393555 13.657463 5.7550456 13.649329 5.6744792 13.643229
            C 5.5714519 13.635429 5.4747722 13.628746 5.2604167 13.622396
            C 4.8304851 13.619496 4.4513347 13.687175 4.1432292 13.830729
            C 3.6302897 14.069743 3.4460042 14.492513 3.3411458 14.833333
            C 3.3360158 14.850183 3.331422 14.868087 3.328125 14.885417 L 3.2916667 15.070312
            C 3.2528487 15.265625 3.2395015 15.329997 3.1953125 15.627604
            C 3.1656495 15.827556 3.1581217 15.927573 3.1510417 16.028646
            C 3.1453017 16.110436 3.1411125 16.192545 3.1171875 16.354167
            C 3.0913085 16.528971 3.0749098 16.616211 3.0598958 16.703125
            C 3.0439048 16.796635 3.02771 16.890625 3 17.078125
            C 2.972168 17.265625 2.9619948 17.35913 2.9505208 17.453125
            C 2.9397788 17.541015 2.9269207 17.629883 2.9010417 17.804688
            C 2.8784587 17.957031 2.8602697 18.026449 2.8385417 18.106771
            C 2.8124187 18.203451 2.7837322 18.313476 2.7526042 18.523438
            C 2.7222082 18.728516 2.7158205 18.837484 2.7109375 18.934896
            C 2.7065475 19.020836 2.7031235 19.094889 2.6796875 19.252604
            C 2.6607665 19.38029 2.6442463 19.457275 2.6302083 19.523438
            C 2.6033533 19.650391 2.5803632 19.76009 2.5651042 20.020833
            C 2.5619342 20.075033 2.5689253 20.129802 2.5833333 20.182292
            C 2.5995683 20.241862 2.611084 20.304931 2.625 20.367188
            C 2.657593 20.513427 2.6970214 20.677653 2.765625 20.846354
            C 2.844727 21.041422 2.9454345 21.249186 3.078125 21.479167
            C 3.2019043 21.693522 3.4036458 21.981283 3.6536458 22.138021
            C 3.6649988 22.145121 3.675537 22.15015 3.6875 22.15625
            C 3.8953857 22.26416 4.0061442 22.309815 4.1119792 22.351562
            C 4.1962082 22.384762 4.276001 22.41528 4.4296875 22.492188
            C 4.5853271 22.570067 4.6527914 22.613282 4.7317708 22.664062
            C 4.8221028 22.722412 4.9237468 22.788574 5.1276042 22.890625
            C 5.3107097 22.982175 5.4025066 23.025635 5.4947917 23.070312
            C 5.5839027 23.113282 5.6744385 23.156167 5.8515625 23.244792
            C 5.9935303 23.315842 6.0811767 23.359456 6.15625 23.395833
            C 6.2666016 23.449543 6.3479411 23.488773 6.5494792 23.59375
            C 6.748698 23.712402 7.0048014 23.775879 7.3776042 23.796875
            C 7.4115392 23.798875 7.4447015 23.799479 7.4765625 23.799479
            C 7.932373 23.799479 8.1855061 23.612305 8.3177083 23.453125
            C 8.4117023 23.384275 8.4630127 23.360186 8.5390625 23.325521
            C 8.6317135 23.283041 8.7447917 23.231287 8.9244792 23.114583
            C 9.1095378 22.99471 9.2044271 22.922852 9.2864583 22.859375
            C 9.3577473 22.804445 9.4276123 22.748698 9.578125 22.651042
            C 9.7510986 22.538493 9.8370362 22.480469 9.921875 22.421875
            C 10.001709 22.366945 10.081829 22.311442 10.244792 22.205729
            C 10.404338 22.101969 10.485798 22.053792 10.567708 22.005208
            C 10.658158 21.951738 10.748454 21.898112 10.924479 21.783854
            C 11.085002 21.679606 11.168091 21.630289 11.25 21.580729
            C 11.3396 21.526529 11.42863 21.473388 11.604167 21.359375
            C 11.781047 21.244629 11.86731 21.183919 11.953125 21.122396
            C 12.030515 21.067216 12.106201 21.009521 12.265625 20.90625
            C 12.390625 20.82495 12.469685 20.777994 12.536458 20.736979
            C 12.653889 20.664469 12.738526 20.612061 12.953125 20.460938
            C 13.159668 20.315675 13.24585 20.246989 13.34375 20.166667
            C 13.40613 20.115397 13.476685 20.055907 13.601562 19.960938
            C 13.758178 19.84204 13.837443 19.784913 13.916667 19.726562
            C 13.998697 19.666262 14.079589 19.605306 14.242188 19.481771
            C 14.394286 19.366293 14.472981 19.311768 14.552083 19.257812
            C 14.638383 19.199223 14.724894 19.139323 14.885417 19.018229
            C 15.140299 18.83195 15.334758 18.544597 15.416667 18.229167
            C 15.502847 17.898113 15.479253 17.549804 15.351562 17.273438
            C 15.239639 17.031128 15.082774 16.829285 14.901042 16.671875 L 15.130208 16.671875
            L 15.132812 16.671875 C 15.147343 16.671875 15.159949 16.673142 15.174479 16.671875
            L 17.276042 16.486979 C 17.527995 16.465009 17.72347 16.258138 17.731771 16.005208
            L 17.875 11.346354 L 18.080729 9
            C 18.082229 8.983643 18.083433 8.9668778 18.083333 8.9505208 L 18.067708 7.4583333
            L 18.903646 8.0130208 C 19.021688 8.0911458 19.167602 8.1158854 19.304688 8.0807292
            C 19.441772 8.0453292 19.556722 7.9536133 19.622396 7.828125 L 20.455729 6.2369792
            L 21.361979 4.7395833 C 21.485636 4.5354817 21.449587 4.2728679 21.273438 4.1119792
            L 19.648438 2.6302083 L 17.893229 1.1692708
            C 17.836959 1.1223958 17.771322 1.0888665 17.700521 1.0703125 L 15.296875 0.44010417
            C 15.256271 0.37418597 15.201146 0.31349693 15.130208 0.27083333
            C 15.039617 0.21622208 14.939082 0.19397765 14.841146 0.20052083 z" />
    </g>
    {/* The gap between the hand and the shirt hem: the fill layer is one silhouette of shirt + hand,
        so this pocket of background came out white. Painted back to the disc, under the line work. */}
    <path fill="currentColor" d="M11.13,15.33L12.84,15.38L12.91,15.40L12.96,15.49L12.93,15.57L12.81,15.66L12.06,16.05
          L11.82,16.23L11.71,16.27L11.64,16.25L11.49,16.08L11.38,16.00L11.17,15.79L11.12,15.71
          L11.01,15.45L11.04,15.38 Z" />
    <g transform="translate(3 3) scale(0.75)" fill="#2B2B2B">
      <path d="M21.2725277,4.1117964 l-1.6235352-1.482666 L17.8936214,1.168681
            c-0.0562744-0.046875-0.1221924-0.0808105-0.1929932-0.0993652 l-2.4035797-0.63031
            c-0.0406036-0.065918-0.0951843-0.1243896-0.1661224-0.1670532
            c-0.1207886-0.072815-0.259613-0.0877686-0.385788-0.0541992
            c-0.1600647-0.0404663-0.3291931,0.0010986-0.4525299,0.1107788
            c-0.1096954,0.0646973-0.1950836,0.1708374-0.2299194,0.3035278
            c-0.0010071,0.0038452-0.0009003,0.0076904-0.0018158,0.0115356
            c-0.3303223,0.4616393-1.0143585,1.2103271-1.7542877,1.2926025
            c-0.5391083-0.0524292-1.0881042-0.4899292-1.6170654-1.2992554
            c-0.1202393-0.1842651-0.3411102-0.2607422-0.5427856-0.2087708
            c-0.1317749-0.0343933-0.2765045-0.0167542-0.399353,0.0637512
            c-0.0140991,0.0092163-0.025589,0.0206909-0.0384369,0.0310059 L8.4568539,0.7568159
            c-0.013916,0.0024414-0.02771,0.0056152-0.0412598,0.0095215 L6.8273377,1.2048138
            c-0.0581055,0.0161133-0.112793,0.0424805-0.161499,0.0778809 L4.9567323,2.5285444
            c-0.008667,0.0063477-0.0170898,0.0129395-0.0252686,0.0197754 l-1.614502,1.3435059
            C3.1278749,4.0490522,3.0813661,4.3205366,3.2070985,4.5319624 L4.2681336,6.314189
            l1.0532227,1.7241211 c0.0777588,0.1271973,0.208252,0.2131348,0.355835,0.2341309
            c0.1481934,0.0214844,0.2969971-0.0246582,0.4073486-0.1252441 l0.5256348-0.4782715
            l0.0847168,1.4208984 l-0.1387939,2.4787598
            c-0.0012207,0.0231934-0.0009766,0.0463867,0.0009766,0.0695801 l0.1717529,2.0552979
            l-0.0026855-0.0001221 l-0.7255859-0.0302734
            c-0.1606445-0.0065918-0.2463379-0.0129395-0.3269043-0.019043
            c-0.1030273-0.0078125-0.1986084-0.0148926-0.4129639-0.0212402
            c-0.4299316-0.0029297-0.8082275,0.0654297-1.116333,0.2089844
            c-0.5129395,0.2390137-0.6990967,0.6606445-0.8039551,1.0014648
            c-0.005127,0.0168457-0.0095215,0.0339355-0.0128174,0.0512695 l-0.0368652,0.1870117
            c-0.0388184,0.1953125-0.0513916,0.2583008-0.0955811,0.5559082
            c-0.0296631,0.1999512-0.0366211,0.3012695-0.0437012,0.4023428
            c-0.0057373,0.0817871-0.0114746,0.1635742-0.0354004,0.3251953
            c-0.0258789,0.1748047-0.0408936,0.262207-0.0559082,0.3491211
            c-0.0159912,0.0935059-0.0321045,0.1865234-0.0598145,0.3740234
            c-0.027832,0.1875-0.0391846,0.2817383-0.0506592,0.3757324
            c-0.0107422,0.0878906-0.0214844,0.1755371-0.0473633,0.3503418
            c-0.022583,0.1523438-0.0412598,0.2216797-0.0629883,0.302002
            c-0.026123,0.0966797-0.0556641,0.2062988-0.086792,0.4162598
            c-0.0303955,0.2050781-0.0360107,0.3156738-0.0408936,0.4130859
            c-0.0043945,0.0859375-0.0081787,0.1601562-0.0316162,0.3178711
            c-0.0189209,0.1276855-0.0351562,0.2045898-0.0491943,0.270752
            c-0.0268555,0.1269531-0.0499268,0.2365723-0.0651855,0.4973145
            c-0.0031738,0.0541992,0.0025635,0.1086426,0.0169678,0.1611328
            c0.0162354,0.0595703,0.0297852,0.1210938,0.0437012,0.1833496
            c0.0325928,0.1462402,0.0695801,0.3120117,0.1381836,0.4807129
            c0.0791016,0.1950684,0.1820068,0.4013672,0.3146973,0.6313477
            c0.1237793,0.2143555,0.3237305,0.5026855,0.5737305,0.6594238
            c0.0113525,0.0070801,0.0231934,0.013916,0.0351562,0.0200195
            c0.2078857,0.1079102,0.3173828,0.151123,0.4232178,0.1928711
            c0.0842285,0.0332031,0.1636963,0.0644531,0.3173828,0.1413574
            c0.1556396,0.0778809,0.223877,0.1218262,0.3028564,0.1726074
            c0.090332,0.0583496,0.192749,0.1242676,0.3966064,0.2263184
            c0.1831055,0.0915527,0.2752686,0.1359863,0.3675537,0.1806641
            c0.0891113,0.0429688,0.1782227,0.0861816,0.3553467,0.1748047
            c0.1419678,0.0710449,0.2293701,0.1135254,0.3044434,0.1499023
            c0.1103516,0.0537109,0.1923828,0.0935059,0.3939209,0.1984863
            c0.1992188,0.1186523,0.4562988,0.1811523,0.8291016,0.2021484
            c0.0339355,0.0019531,0.0667725,0.0026855,0.0986328,0.0026855
            c0.4558105,0,0.7081294-0.1870117,0.8403316-0.3461914
            c0.0939941-0.0688477,0.1442871-0.0917969,0.2203369-0.1264648
            c0.0926514-0.0424805,0.2080078-0.0952148,0.3876953-0.2119141
            c0.1850586-0.119873,0.2780762-0.1916504,0.3601074-0.255127
            c0.0712891-0.0549316,0.1425781-0.1098633,0.2930908-0.2075195
            c0.1729736-0.1125488,0.2583008-0.1713867,0.3431396-0.2299805
            c0.079834-0.0549316,0.1593018-0.1098633,0.3222656-0.2155762
            c0.1595459-0.1037598,0.241333-0.1520996,0.3232422-0.2006836
            c0.0904541-0.0534668,0.1810303-0.1071777,0.3570557-0.2214355
            c0.1605225-0.104248,0.2427979-0.1538086,0.324707-0.2033691
            c0.0895996-0.0541992,0.178833-0.1081543,0.3543701-0.222168
            c0.1768799-0.1147461,0.2630615-0.1762695,0.348877-0.237793
            c0.0773926-0.0551758,0.154541-0.1103516,0.3139648-0.213623
            c0.125-0.0812988,0.2030029-0.1293945,0.2697754-0.1704102
            c0.1174316-0.0725098,0.2034912-0.1252441,0.4180908-0.2763672
            c0.206543-0.1452637,0.2911377-0.2145996,0.3890381-0.2949219
            c0.0623779-0.0512695,0.1334229-0.1096191,0.2583008-0.2045898
            c0.1566162-0.1188965,0.2357178-0.1772461,0.3149414-0.2355957
            c0.0820312-0.0603027,0.1641846-0.1208496,0.3267822-0.2443848
            c0.1520996-0.1154785,0.2294922-0.1682129,0.3085938-0.222168
            c0.0863037-0.0585938,0.1743164-0.1186523,0.3348389-0.2397461
            c0.2548828-0.1862793,0.4481201-0.4743652,0.5300293-0.7897949
            c0.0861816-0.3310547,0.0622559-0.6794434-0.0654297-0.9558105
            c-0.1119232-0.2423096-0.268631-0.4447632-0.4503632-0.6021729 l0.2280731,0.0013428 h0.0029297
            c0.0145264,0,0.0290527-0.0007324,0.0435791-0.0019531 l2.1013184-0.1835938
            c0.2519531-0.0219727,0.447876-0.2287598,0.4561768-0.4816895 l0.1416016-4.659667
            l0.2072754-2.3469238 c0.0014648-0.0163574,0.0020752-0.0327148,0.0019531-0.0490723
            l-0.0153809-1.4914551 l0.8353271,0.5539551
            c0.118042,0.078125,0.2635498,0.1027832,0.4006348,0.067627
            c0.137085-0.0354004,0.2528076-0.1269531,0.3184814-0.2524414 l0.8326416-1.5895996
            l0.9073486-1.4980469 C21.486517,4.5358686,21.4486752,4.2726851,21.2725277,4.1117964 z
            M14.4487972,17.9777622 c-0.0241699,0.0935059-0.0826416,0.1833496-0.1583252,0.2387695
            c-0.1472168,0.111084-0.2249756,0.1638184-0.3013916,0.2158203
            c-0.0899658,0.0612793-0.1781006,0.1213379-0.3508301,0.2526855
            c-0.1566162,0.1188965-0.2357178,0.1772461-0.3149414,0.2355957
            c-0.0820312,0.0603027-0.1641846,0.1208496-0.3266602,0.2441406
            c-0.1394043,0.105957-0.2185059,0.1708984-0.2880859,0.2280273
            c-0.0891113,0.0732422-0.1535645,0.1262207-0.3295898,0.25
            c-0.1887207,0.1325684-0.2641602,0.1791992-0.3675537,0.2429199
            c-0.0716553,0.0441895-0.1555176,0.0957031-0.2896729,0.1828613
            c-0.1779785,0.1154785-0.2641602,0.177002-0.3505859,0.2387695
            c-0.0769043,0.0549316-0.1540527,0.1101074-0.3126221,0.2131348
            c-0.1617432,0.1049805-0.2440186,0.1547852-0.3265381,0.2045898
            c-0.0888672,0.0537109-0.1779785,0.1074219-0.3521729,0.220459
            c-0.1588135,0.1032715-0.2406006,0.1516113-0.3221436,0.1999512
            c-0.0908203,0.0539551-0.1815186,0.107666-0.3581543,0.2224121
            c-0.1745605,0.1132812-0.2598877,0.1721191-0.3455811,0.2312012
            c-0.0792236,0.0546875-0.1586914,0.1096191-0.3199463,0.2141113
            c-0.1844482,0.119873-0.2772217,0.1914062-0.3591309,0.2546387
            c-0.0714111,0.0551758-0.1428223,0.1101074-0.2938232,0.2080078
            c-0.118042,0.0766602-0.1833496,0.1066895-0.2590332,0.1411133
            c-0.1162109,0.0532227-0.2479243,0.1135254-0.4689937,0.284668
            c-0.0338135,0.0263672-0.064209,0.0568848-0.09021,0.0908203
            c-0.0212402,0.0041504-0.0626221,0.0092773-0.1280518,0.0056152
            c-0.2875977-0.0161133-0.3668213-0.0588379-0.3747559-0.0634766
            c-0.0109863-0.0068359-0.0222168-0.0131836-0.0336914-0.0192871
            c-0.2253418-0.1174316-0.3138428-0.1604004-0.4338379-0.21875
            c-0.0723877-0.0351562-0.1568604-0.0761719-0.2941895-0.1450195
            c-0.1831055-0.0915527-0.2752686-0.1359863-0.3675537-0.1806641
            c-0.0891113-0.0429688-0.1782227-0.0861816-0.3552246-0.1748047
            c-0.1552734-0.0776367-0.2233887-0.121582-0.3023682-0.1723633
            c-0.0904541-0.0583496-0.1931152-0.1245117-0.3973389-0.2263184
            c-0.192749-0.0966797-0.3015137-0.1394043-0.397583-0.1774902
            c-0.0847168-0.0334473-0.1585693-0.0625-0.3027344-0.1362305
            c-0.0340576-0.0275879-0.1235352-0.1206055-0.2303467-0.3056641
            c-0.109375-0.1894531-0.1925049-0.3554688-0.2541504-0.5073242
            c-0.0372314-0.0917969-0.0622559-0.2038574-0.0887451-0.3225098
            c-0.0108643-0.0488281-0.0217285-0.0969238-0.0333252-0.1445312
            c0.0097656-0.1281738,0.0214844-0.1833496,0.0402832-0.2727051
            c0.0170898-0.0808105,0.0369873-0.1750488,0.0600586-0.3308105
            c0.0303955-0.2055664,0.0360107-0.3161621,0.0410156-0.4138184
            c0.0043945-0.0856934,0.0081787-0.1599121,0.0314941-0.3173828
            c0.022583-0.1523438,0.0412598-0.2216797,0.0629883-0.302002
            c0.026123-0.0966797,0.0556641-0.2062988,0.086792-0.4162598
            c0.027832-0.1875,0.0391846-0.2817383,0.0506592-0.3757324
            c0.0107422-0.0878906,0.0214844-0.1755371,0.0473633-0.3503418
            c0.026001-0.1760254,0.0410156-0.2631836,0.0561523-0.3508301
            c0.0158691-0.0927734,0.0319824-0.185791,0.0595703-0.3720703
            c0.0296631-0.1999512,0.0366211-0.3012695,0.0437012-0.4023438
            c0.0057373-0.0817871,0.0114746-0.1635733,0.0354004-0.3254385
            c0.0402832-0.2714844,0.0516357-0.3291016,0.0870361-0.5075684 l0.0321045-0.1625977
            c0.0769043-0.239502,0.1525879-0.3139648,0.2630615-0.3654785
            c0.1711426-0.0795898,0.4013672-0.1181641,0.6737061-0.1157227
            c0.1821289,0.0053711,0.2669678,0.0117188,0.3583984,0.0185547
            c0.0891113,0.0068359,0.18396,0.013916,0.3615723,0.0209961 l0.7255859,0.0302734
            c0.1750488,0.0070801,0.2625732,0.0129395,0.3499756,0.0185547
            c0.0935059,0.0061035,0.1870117,0.0119629,0.3740234,0.0195312
            c0.1776123,0.0073242,0.2663574,0.012207,0.3551025,0.0168457
            c0.0921631,0.005127,0.1844482,0.0102539,0.3690181,0.0175781
            c0.1964111,0.0083008,0.2946777,0.0063477,0.3930664,0.0041504
            c0.0830078-0.0012207,0.1661377-0.003418,0.3304443,0.003418
            c0.2268066,0.0109863,0.4171143-0.1096191,0.5056152-0.3034668
            c0.0855713-0.1708984,0.1304932-0.2541504,0.1757812-0.3383789
            c0.0479736-0.0891113,0.0964355-0.1789551,0.1871338-0.3598633
            c0.0787354-0.1567383,0.1213379-0.2263184,0.1707764-0.3068848
            c0.0537109-0.0874023,0.114502-0.1865234,0.2122803-0.3815918
            c0.0948486-0.1889648,0.1365967-0.286377,0.1782227-0.3837891
            c0.0357666-0.0834961,0.0715332-0.1669922,0.1530762-0.3293457
            c0.0871582-0.1738281,0.1314697-0.2597656,0.1761475-0.3459473
            c0.0456543-0.0881348,0.0914307-0.1767578,0.1804199-0.354248
            c0.0892334-0.1777344,0.1329346-0.267334,0.1766357-0.3566895
            c0.0426025-0.0871582,0.085083-0.1740723,0.171875-0.347168
            c0.0965576-0.1923828,0.1375732-0.2919922,0.1787109-0.3916016
            c0.0338135-0.0822754,0.067749-0.1647949,0.1472168-0.3232422
            c0.0744629-0.1484375,0.1181641-0.21875,0.1645508-0.293457
            c0.05896-0.0952148,0.119873-0.1933594,0.2200928-0.401123
            c0.0045166-0.0092773,0.008667-0.0185547,0.0124512-0.0280762
            c0.0339355-0.0830078,0.1226807-0.2009277,0.2371826-0.2636719
            c0.0900879-0.0498047,0.1855469-0.0495605,0.2402344-0.0266113
            c0.0524902,0.0212402,0.107666,0.0334473,0.1630859,0.036377
            c0.0770264,0.0541992,0.1452637,0.121582,0.1859131,0.1867676
            c0.0175781,0.0283203,0.0441895,0.0708008-0.0328369,0.2260742
            c-0.088623,0.1945801-0.1242676,0.2841797-0.1643066,0.3845215
            c-0.029541,0.0739746-0.0616455,0.154541-0.1247559,0.2990723
            c-0.0722656,0.1655273-0.1094971,0.2470703-0.1470947,0.3291016
            c-0.0388184,0.0847168-0.0780029,0.170166-0.152832,0.3413086
            c-0.0811768,0.1865234-0.1143799,0.282959-0.1474609,0.3793945
            c-0.0266113,0.0773926-0.0531006,0.1547852-0.1184082,0.3044434
            c-0.0661621,0.1516113-0.1047363,0.2250977-0.1431885,0.298584
            c-0.0467529,0.0893555-0.0935059,0.1784668-0.1739502,0.362793
            c-0.0800781,0.1833496-0.1138916,0.277832-0.1478271,0.3720703
            c-0.0281982,0.0786133-0.0563965,0.1572266-0.1228027,0.3093262
            c-0.0603027,0.1384277-0.0949707,0.1960449-0.1386719,0.269043
            c-0.0522461,0.0871582-0.1113281,0.185791-0.1975098,0.3830566
            c-0.0804443,0.1843262-0.1138916,0.279541-0.1474609,0.3747559
            c-0.0273438,0.0776367-0.0546875,0.1555176-0.1203613,0.3059082
            c-0.0662842,0.1518555-0.1047363,0.2255859-0.1431885,0.2990723
            c-0.0466309,0.0891113-0.0932617,0.1782227-0.1737061,0.3623047
            c-0.0826416,0.1896973-0.1153564,0.2966309-0.1442871,0.3908691
            c-0.0250244,0.081543-0.0447998,0.1462402-0.1064453,0.2834473
            c-0.1168213,0.2387695-0.244873,0.5556631-0.2138672,0.9047842
            c0.0328369,0.3728027,0.2119141,0.670166,0.3433838,0.861084
            c0.1655273,0.2407227,0.3736572,0.421875,0.5734863,0.5810547
            c0.0512695,0.0412598,0.0803223,0.0822754,0.1203613,0.1394043
            c0.100708,0.1433105,0.3370361,0.4782715,0.8753662,0.3457031
            c0.305542-0.0751953,0.4425049-0.1699219,0.5899658-0.2856445
            c0.0565186-0.0444336,0.1053467-0.0827637,0.1993408-0.1333008
            c0.1624756-0.0871582,0.244751-0.1286621,0.3272705-0.1699219
            c0.0882568-0.0444336,0.1765137-0.0888672,0.3505859-0.182373
            c0.2070312-0.111084,0.2982178-0.1711426,0.3946533-0.2346191
            c0.0657959-0.0432129,0.1383057-0.0908203,0.2723389-0.1687012
            c0.0152588-0.0087891,0.0299072-0.0183105,0.0441895-0.0288086
            c0.0369873-0.0273438,0.1343994-0.0522461,0.2351074-0.0024414
            c0.0395508,0.0192871,0.1768799,0.1005859,0.2835693,0.3312988
            C14.474432,17.7599888,14.4765072,17.8715611,14.4487972,17.9777622 z M13.0590267,16.7262974
            c-0.0841064,0.0551758-0.1505127,0.098877-0.3183594,0.189209
            c-0.1622314,0.0869141-0.2445068,0.128418-0.3267822,0.1696777
            c-0.088501,0.0444336-0.1767578,0.0888672-0.3510742,0.1826172
            c-0.1705322,0.0913086-0.270874,0.170166-0.3441162,0.2277832
            c-0.0394287,0.0310059-0.0622559,0.0490723-0.0931396,0.0634766
            c-0.0576172-0.0756836-0.1364746-0.1665039-0.2507324-0.2578125
            c-0.130249-0.1040039-0.2773438-0.2265625-0.3736572-0.3666992
            c-0.0933838-0.135437-0.1444855-0.2438965-0.1629639-0.3390503 l2.1260986,0.0646362
            l0.1949921,0.0011597 C13.122427,16.6847935,13.0895138,16.7062778,13.0590267,16.7262974 z
            M19.5921078,5.7333784 c-0.0053711,0.0087891-0.010498,0.0178223-0.0152588,0.0270996
            l-0.5825195,1.1120605 l-1.1594238-0.7685547
            c-0.1539307-0.1022949-0.3521729-0.111084-0.5150146-0.0224609
            c-0.1625977,0.0883789-0.2630615,0.2592773-0.2612305,0.4443359 l0.0249023,2.409668
            l-0.2064209,2.3374023 c-0.0008545,0.0100098-0.0014648,0.0197754-0.001709,0.0297852
            l-0.1274414,4.2270508 l-1.6364746,0.1428223 l-2.1290283-0.012207 l-1.8253937-0.055603
            c0.014679-0.0397644,0.0323486-0.0845032,0.0580597-0.1433716
            c0.0662842-0.1518555,0.1047363-0.2255859,0.1431885-0.2990723
            c0.0466309-0.0891113,0.0932617-0.1782227,0.173584-0.3623047
            s0.1137695-0.2792969,0.1472168-0.3742676
            c0.0274658-0.0778809,0.0548096-0.1557617,0.1206055-0.3063965
            c0.0603027-0.1384277,0.0949707-0.1960449,0.1386719-0.269043
            c0.0522461-0.0871582,0.1113281-0.185791,0.1976318-0.3830566
            c0.0797119-0.1831055,0.1136475-0.2773438,0.1473389-0.371582
            c0.0283203-0.0786133,0.0565186-0.1572266,0.1231689-0.3100586
            c0.0661621-0.1516113,0.1047363-0.2250977,0.1431885-0.298584
            c0.0467529-0.0893555,0.0935059-0.1784668,0.1739502-0.362793
            c0.0814209-0.1865234,0.114502-0.282959,0.1477051-0.3796387
            c0.0266113-0.0773926,0.0531006-0.1547852,0.1181641-0.3039551
            c0.071167-0.1628418,0.1085205-0.2443848,0.1455078-0.3251953
            c0.0394287-0.0861816,0.0786133-0.1716309,0.1544189-0.345459
            c0.0693359-0.1586914,0.1046143-0.2470703,0.137085-0.3283691
            c0.0354004-0.0891113,0.0671387-0.168457,0.1379395-0.324707
            c0.263916-0.5288086,0.1341553-0.9367676-0.0212402-1.185791
            c-0.1762695-0.2827148-0.4755859-0.5307617-0.8005371-0.6638184
            c-0.0874023-0.0354004-0.1782227-0.0478516-0.2822266-0.0290527
            c-0.2785645-0.0449219-0.5762939,0.0065918-0.8355713,0.1486816
            c-0.2938232,0.1611328-0.545166,0.4389648-0.675293,0.7460938
            c-0.0736084,0.1513672-0.1134033,0.2158203-0.1635742,0.2966309
            c-0.0533447,0.0859375-0.1137695,0.1835938-0.2086182,0.3723145
            c-0.0960693,0.1916504-0.137085,0.2912598-0.1779785,0.390625
            c-0.0339355,0.0825195-0.0678711,0.1647949-0.1477051,0.3239746
            c-0.0892334,0.1777344-0.1329346,0.267334-0.1766357,0.3566895
            c-0.0426025,0.0871582-0.085083,0.1740723-0.1719971,0.3474121
            c-0.0861816,0.1716309-0.1306152,0.2575684-0.1748047,0.3427734
            c-0.0461426,0.0893555-0.0919189,0.1779785-0.1818848,0.3571777
            c-0.0949707,0.189209-0.1367188,0.2868652-0.1784668,0.3842773
            c-0.0357666,0.0834961-0.0714111,0.1669922-0.15271,0.3291016
            c-0.0787354,0.1567383-0.1213379,0.2263184-0.1707764,0.3068848
            c-0.0537109,0.0874023-0.114502,0.1865234-0.2122803,0.3815918
            C8.7479916,13.4902143,8.7030697,13.5737104,8.658514,13.65623
            c-0.0189209,0.0351562-0.0378418,0.0703125-0.0595703,0.1115723
            c-0.0321045,0.0004883-0.0616455,0.0009766-0.0910645,0.0014648
            c-0.0830078,0.001709-0.166626,0.0031738-0.333374-0.003418
            c-0.1776118-0.0073242-0.2663569-0.012207-0.3551021-0.0168457
            c-0.0265808-0.0014648-0.0539398-0.0029907-0.0827484-0.0045166 l-0.180069-2.1549072
            l0.1384277-2.4729004 c0.0009766-0.019043,0.0009766-0.0383301-0.0001221-0.0576172
            L7.5462098,6.5651655 c-0.0114746-0.1921387-0.1323242-0.3608398-0.310791-0.4333496
            C7.0573182,6.0593061,6.8528504,6.0956831,6.7105165,6.2253218 L5.859076,7.0002241
            L5.1244569,5.7975874 L4.2848573,4.387187 l1.2739258-1.0600586 l1.6225586-1.1826172
            l1.47998-0.4086915 l1.1762848-0.2197266
            c0.7053375,0.946808,1.4891052,1.4301759,2.3360443,1.4301759
            c0.0146484,0,0.029541-0.0002441,0.0443115-0.0004883
            c0.032074-0.0006104,0.0626526-0.0063477,0.0943298-0.0083618
            c0.037262,0.0018921,0.0738525,0.0088501,0.1113586,0.0088501
            c0.0146484,0,0.029541-0.0002441,0.0443115-0.0004883
            c1.166626-0.0228882,2.0782776-0.9725953,2.5278931-1.551941 l2.343689,0.6146852
            l1.6523438,1.3740234 l1.3029785,1.1904297 L19.5921078,5.7333784 z" />
    </g>
  </svg>
)

export const ArtPackStyles = (p: IconProps) => (
  <svg viewBox="0 0 24 24" {...p}>
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <g transform="translate(3 3) scale(0.75)" fill="#fff">
      <path d="M 7.5010858,18.611561 4.7591424,17.392933
            c -0.084595,-0.240479 -0.069092,-0.631226 -0.011963,-0.994995 l 2.7539062,1.223999
            v 0.989624 z" />
      <path d="M 8.2042103,16.840076 4.5978875,15.237171
            c -0.1304932,-0.05786 -0.2796631,-0.05749 -0.409668,0.0016 -0.130127,0.05896 -0.2286379,0.170776 -0.2709963,0.307129 -0.085083,0.274414 -0.4822998,1.685058 0.060181,2.507446 0.052612,0.07983 0.1269534,0.142822 0.2142336,0.181641
            l 3.6063232,1.602905
            c 0.064941,0.02881 0.1341553,0.04309 0.2031246,0.04309 0.095215,0 0.1900635,-0.02722 0.2724609,-0.08081 0.1419678,-0.09216 0.2275391,-0.249878 0.2275391,-0.41919
            v -2.083984 c 0,-0.197632 -0.116333,-0.376709 -0.296875,-0.45691 z" />
      <path d="M 13.846354 0.052083333 C 13.728263 0.063704794 13.614838 0.11783854 13.53125 0.20572917
            C 13.374863 0.37053937 12.116506 1.7288411 11.809896 2.9661458
            C 11.685766 3.0036668 11.548768 3.0478667 11.382812 3.1015625
            C 10.866578 3.2685546 10.229289 3.4835612 9.5885417 3.7057292
            C 8.5636393 4.0610759 6.6604411 4.7362874 5.4401042 5.2786458
            C 5.3698527 5.3098805 5.3124847 5.3567098 5.265625 5.4114583
            C 3.8933866 4.8007405 2.4612579 4.2157745 1.2109375 3.8125
            C 1.03125 3.7543945 0.83060707 3.8023682 0.69791667 3.9375
            C 0.56534827 4.0727539 0.52168783 4.2714436 0.58333333 4.4505208
            C 0.58919233 4.4674888 1.1769205 6.1680908 1.8072917 7.6875
            C 1.8326217 7.748535 1.8678488 7.7993317 1.9114583 7.84375
            C 1.8726553 7.911697 1.847244 7.9896643 1.84375 8.0729167
            C 1.8396 8.1710617 1.4477947 17.926799 1.9713542 19.322917
            C 2.0034582 19.408607 2.0581866 19.482544 2.1302083 19.539062
            C 3.9562581 20.974487 11.542562 23.799765 11.864583 23.919271
            C 11.866983 23.920141 11.867392 23.918371 11.869792 23.919271
            C 11.873692 23.920671 11.878843 23.923179 11.882812 23.924479
            C 11.934143 23.941229 11.985052 23.950521 12.036458 23.950521 L 12.039062 23.950521
            C 12.067583 23.95043 12.09648 23.947808 12.125 23.942708
            C 16.090088 23.238362 21.653157 19.762166 21.888021 19.614583
            C 22.013143 19.535843 22.096924 19.404174 22.117188 19.257812
            C 22.419311 17.067626 22.496501 10.189737 22.408854 7.6041667
            C 22.405254 7.4991099 22.368286 7.4060516 22.309896 7.328125
            C 22.313896 7.320785 22.321861 7.3148907 22.325521 7.3072917
            C 23.017659 5.8695471 23.413085 4.2729899 23.429688 4.2057292
            C 23.47351 4.0273845 23.414632 3.8395589 23.278646 3.7161458
            C 20.254598 0.97371423 14.218913 0.0908203 13.963542 0.0546875
            C 13.923594 0.0487976 13.885718 0.048209513 13.846354 0.052083333 z" />
    </g>
    <g transform="translate(3 3) scale(0.75)" fill="#2B2B2B">
      <path d="M8.2042103,16.8400764 l-3.6063228-1.6029053
            c-0.1304932-0.0578613-0.2796631-0.0574951-0.409668,0.0015869
            c-0.130127,0.05896-0.2286379,0.1707764-0.2709963,0.3071289
            c-0.085083,0.2744141-0.4822998,1.6850586,0.0601807,2.5074463
            c0.0526125,0.079834,0.1269534,0.1428223,0.2142336,0.1816406 l3.6063232,1.6029053
            C7.8629022,19.8666878,7.932116,19.88097,8.0010853,19.88097
            c0.0952148,0,0.1900635-0.0272217,0.2724609-0.0808105
            c0.1419678-0.0921631,0.2275391-0.2498779,0.2275391-0.4191895 v-2.0839844
            C8.5010853,17.0993538,8.3847523,16.9202766,8.2042103,16.8400764 z M7.5010858,18.6115608
            l-2.7419434-1.2186279 c-0.0845947-0.2404785-0.0690918-0.6312256-0.0119629-0.9949951
            l2.7539062,1.223999 V18.6115608 z" />
      <path d="M23.4293079,4.2055545 c0.0438232-0.1783447-0.0136719-0.366333-0.1496582-0.4897461
            c-3.0240479-2.7424316-9.0598145-3.6241455-9.3151855-3.6602783
            c-0.15979-0.0235596-0.3214111,0.0335693-0.4328613,0.1507568
            c-0.1563873,0.1648102-1.4139099,1.5218201-1.72052,2.7591248
            c-0.1241302,0.0375214-0.2635498,0.0821991-0.4295044,0.1358948
            C10.8653431,3.2682986,10.2282581,3.482532,9.5875111,3.7047
            C8.5626087,4.0600467,6.6609979,4.7372928,5.440661,5.2796512
            C5.3704095,5.3108859,5.3122582,5.3572574,5.2653985,5.4120059
            C3.8931601,4.8012881,2.4600241,4.2152743,1.2097037,3.8119998
            c-0.1796875-0.0581055-0.3778076-0.0093994-0.510498,0.1257324
            C0.5666373,4.0729861,0.5218375,4.2715945,0.583483,4.4506717
            c0.0058594,0.0169678,0.5925292,1.7165527,1.2229004,3.2359619
            C1.831713,7.7476687,1.8682272,7.8004947,1.9118369,7.844913
            C1.8730338,7.9128604,1.8475974,7.9896121,1.8441031,8.0728645
            C1.8399527,8.1710091,1.4470084,17.9258919,1.9705679,19.32201
            c0.0321045,0.0856934,0.0871582,0.1610107,0.1591797,0.2175293
            c1.8260498,1.4354248,9.4118652,4.2592773,9.7338867,4.3787842
            c0.0023651,0.0008698,0.0047913,0.0011139,0.0071564,0.0019531
            c0.0039215,0.0013885,0.0079193,0.0019379,0.0118713,0.0032349
            c0.0513306,0.0167542,0.1033936,0.0261841,0.1548004,0.0261841
            c0.0005951,0,0.0011292-0.0002594,0.0017242-0.0002594
            c0.0285187-0.0000916,0.0572815-0.0025177,0.0858002-0.0075531
            c3.9650879-0.7043457,9.5268555-4.180542,9.7617188-4.328125
            c0.1251221-0.0787354,0.2089844-0.2086182,0.229248-0.3549805
            c0.302124-2.1901855,0.3804932-9.0678711,0.2928467-11.6534424
            c-0.0035858-0.1050568-0.039917-0.2004242-0.0983124-0.2783508
            c0.0039978-0.0073395,0.0100555-0.0127563,0.0137177-0.0203552
            C23.0163441,5.8688846,23.4127064,4.2728152,23.4293079,4.2055545 z M14.0827503,1.0870242
            c1.1412354,0.1928711,5.7745361,1.0825195,8.2972412,3.1721191
            c-0.1299438,0.465744-0.4182587,1.4159241-0.8260803,2.3282776
            c-0.7754822-0.5280304-2.6809235-1.6961975-4.8167419-2.0386047
            c-0.1313324-0.0210724-0.2758942-0.0539398-0.4263763-0.0926056
            c0.5389709-0.2515869,1.0950928-0.4489441,1.6574554-0.5839081
            c0.2685547-0.0644531,0.43396-0.3343506,0.3695068-0.6029053
            c-0.0644531-0.2684326-0.3338623-0.4342041-0.6029053-0.3695068
            c-0.942276,0.2261658-1.8678894,0.6101227-2.7352905,1.120224
            c-0.8736115-0.3392639-1.7355804-0.7447052-2.1797791-0.9602356
            C13.0416737,2.3772159,13.6850605,1.5417819,14.0827503,1.0870242 z M12.2260609,3.8832889
            c0.3066711,0.1513977,0.9885254,0.4798279,1.7674255,0.8073883
            c-0.6453857,0.4809875-1.2428741,1.0335846-1.7722778,1.644165
            c0.0198669-0.2429504,0.0307617-0.3830872,0.0415955-0.5225983
            c0.0124512-0.1607666,0.0249023-0.3215332,0.0515137-0.6429443
            c0.0227051-0.2751465-0.1818848-0.5167236-0.4571533-0.5394287
            c-0.2718506-0.0246582-0.5167236,0.1817627-0.5394287,0.4571533
            c-0.0267334,0.3238525-0.0393066,0.4858398-0.0518799,0.6478271
            c-0.0124512,0.1607666-0.0249023,0.3215332-0.0515137,0.6428223
            c-0.0263672,0.3184814-0.0407715,0.4787598-0.0550537,0.6379395
            C11.14464,7.1789432,11.1299915,7.3410525,11.103014,7.6678348
            c-0.0227051,0.2751465,0.1818848,0.5167241,0.4571533,0.5394292
            c0.0049286,0.0004272,0.0098572,0.000351,0.0147858,0.0006104
            c0.0355682,0.0080566,0.0718536,0.0136719,0.1092377,0.0136719
            c0.0330811,0,0.0667725-0.0032959,0.1004639-0.0102539 l1.269043-0.2572026
            c0.3149414-0.0643311,0.4726562-0.0949707,0.6303711-0.1256104
            c0.1600342-0.0311279,0.3200684-0.0623779,0.6394043-0.1275635
            c0.2706299-0.0551758,0.4451904-0.3193359,0.3898926-0.5898438
            c-0.0552979-0.2706299-0.3192139-0.4460449-0.5898438-0.3898926
            c-0.3149414,0.0643311-0.4726562,0.0949707-0.6303711,0.1256104
            c-0.129776,0.025238-0.2610779,0.0508881-0.4757996,0.0943146
            c0.6122589-0.6981049,1.32164-1.3123474,2.0911713-1.8144226
            c0.52771,0.187912,1.0432434,0.3409424,1.4703217,0.4094391
            c1.7435608,0.2796021,3.368454,1.2026825,4.1850891,1.7297668
            c-0.6264343,0.3504486-1.6925507,0.9573827-3.0325012,1.7614446
            c-1.704483,1.0226135-4.4113159,1.9353485-5.4133911,2.2558289
            c-0.8062134-1.147049-1.2345734-2.8220673-1.2389526-2.839325
            c-0.0344238-0.137085-0.1251221-0.2532959-0.2497559-0.3197021
            c-0.1358643-0.0724945-2.058136-1.0938115-4.3956904-2.1804051
            C8.3795033,5.1443057,11.485384,4.1043735,12.2260609,3.8832889 z M10.1688099,8.9063606
            c0.0862122,0.3006287,0.265564,0.8725739,0.5388336,1.5028534
            C7.5017571,8.6028328,2.8430045,7.0783572,2.6089957,7.0023074
            C2.6081412,7.0020328,2.6072714,7.0020328,2.6064169,7.0017581
            C2.3320334,6.324512,2.073046,5.6354709,1.8777945,5.1015506
            C5.1387811,6.3005252,9.2447376,8.4221077,10.1688099,8.9063606 z M2.8422415,8.1366615
            c1.5987093,0.5507355,6.3418732,2.2653809,8.7108002,3.9873962
            c0.0719299,1.8271332,0.1839905,5.7168884,0.022995,6.6804962
            c-0.1605225,0.9605255-0.1157837,2.8478699-0.0736847,3.9081573
            c-1.8993683-0.7252502-7.0647578-2.7470093-8.6309052-3.8667755
            C2.6087515,17.6728249,2.680773,12.0009804,2.8422415,8.1366615 z M21.1544056,18.8901253
            c-0.9770508,0.5943604-5.328125,3.1679688-8.6455078,3.9510498
            c-0.041626-0.9821777-0.0997314-2.9552002,0.0534668-3.8718262
            c0.1872101-1.1203918,0.0529785-5.2212067-0.0036469-6.7125854
            c0.9861755-0.3118286,3.835495-1.2609558,5.6871185-2.3720093
            c1.4614105-0.87677,2.5923309-1.5161438,3.1748505-1.8401337
            C21.4856129,10.7491341,21.4122028,16.6455727,21.1544056,18.8901253 z" />
    </g>
  </svg>
)

export const ArtShipPackage = (p: IconProps) => (
  <svg viewBox="0 0 24 24" {...p}>
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <g transform="translate(3 3) scale(0.75)" fill="#fff">
      <path d="M 22.555326,16.936666 H 21.34561
            c -0.228882,-1.300155 -1.36377,-2.291992 -2.728516,-2.291992 -0.976776,0 -2.424133,0.956147 -2.716858,2.291992
            H 14.698027 V 8.5843716
            c 0.440063,0.049805 1.107055,0.1032715 1.94226,0.1032715 1.054566,0 2.073243,-0.084717 2.614502,-0.1386719 0.16626,0.4454346 0.476807,1.2208252 0.89148,2.0098878 0.727539,1.384033 1.477905,2.232788 2.237671,2.533203 -0.07385,0.564575 -0.141846,1.52356 0.08691,2.329224 0.176635,0.621826 0.139404,1.178711 0.08447,1.51538
            z" />
      <path d="m 19.393339,19.006247
            c -0.358154,0.274047 -0.581665,0.218383 -0.654907,0.199829 -0.444458,-0.111084 -1.894531,-0.56836 -1.894531,-1.788086 0,-0.970459 1.17395,-1.773316 1.773193,-1.773316 0.977661,0 1.772949,0.795411 1.773194,1.773072 -0.0044,0.215698 -0.401734,1.132934 -0.996949,1.588501
            z" />
      <path d="m 13.529692,16.922628
            c -1.252991,-0.03282 -4.4511111,-0.104065 -6.1511992,-0.03216 -0.3701324,-1.527557 -1.227417,-2.245789 -2.673996,-2.245789 -1.3360598,0 -2.3020175,0.949311 -2.4627993,2.353974
            C 1.9738295,16.987982 1.730406,16.97724 1.5295698,16.967794 1.6297895,16.112081 1.660185,14.381857 1.5895063,10.767355 1.5425092,8.3580531 1.4596235,5.9426479 1.4203168,4.866476 2.2510053,4.9491176 3.7726117,5.0663051 4.7109665,4.9326381 6.1490769,4.7271938 11.817289,4.7788295 13.529692,4.7993373
            V 16.922628 Z" />
      <path d="m 6.1754441,18.518942
            c -0.3513184,0.414673 -0.914917,0.672241 -1.4709473,0.672241 -1.0991213,0 -1.489136,-0.9552 -1.489136,-1.773193 0,-0.817994 0.3900146,-1.773316 1.489136,-1.773316 0.9315186,0 1.5213623,0.303589 1.7800293,1.855469 0,0 0,0 0,1.22e-4 0.075195,0.451538 -0.1265869,0.803101 -0.309082,1.018677
            z" />
      <path d="m 23.432768,15.148092
            c -0.273071,-0.961914 0.0055,-2.317749 0.0083,-2.331299 0.03003,-0.143066 -0.004,-0.292114 -0.09314,-0.407836 -0.08911,-0.115723 -0.224365,-0.187012 -0.370239,-0.19458 -1.020264,-0.05274 -2.316284,-2.5832525 -2.910767,-4.3631597 -0.0752,-0.225708 -0.300659,-0.3665771 -0.535034,-0.3381348 -0.01428,0.00183 -1.441528,0.1745605 -2.891602,0.1745605 -1.003173,0 -1.750701,-0.082748 -2.110595,-0.1326294
            V 4.3059292 c 0,-0.2730713 -0.219116,-0.4957278 -0.49231,-0.4998782
            C 13.72415,3.8012903 6.338408,3.6899621 4.5696092,3.9426477 3.3885787,4.111227 0.9855025,3.8127649 0.9613326,3.8098352 0.8164351,3.792135 0.6692183,3.8382776 0.5608199,3.9375207 0.4526656,4.0368862 0.3935835,4.178854 0.3993208,4.3257046 0.6212447,9.9450893 0.72769,16.269064 0.4962447,17.14028
            c -0.0817871,0.111206 -0.1162109,0.254272 -0.0871582,0.396362 0.0454102,0.222656 0.2352295,0.386597 0.4619141,0.399292 0,0 0.5872803,0.03271 1.3905182,0.06432 0.2142029,1.315231 1.158783,2.190933 2.4429781,2.190933 0.8419189,0 1.697876,-0.393066 2.2338867,-1.025879 0.3227081,-0.38092 0.5086365,-0.821976 0.5504761,-1.278778 2.0894465,-0.08115 6.4786983,0.04842 6.5258173,0.0499 0.03438,0.001 0.06863,-0.0019 0.102295,-0.0079 0.02653,0.0043 0.05328,0.0082 0.08105,0.0082
            h 1.704422
            c 0.234756,1.051819 1.182754,1.886872 2.593429,2.239624 0.123535,0.03088 0.248291,0.04614 0.373413,0.04614 0.388672,0 0.781128,-0.148071 1.152344,-0.437866 0.573685,-0.447983 1.062256,-1.236038 1.266312,-1.847901
            h 1.663864
            c 0.212646,0 0.401977,-0.134399 0.472046,-0.335205 0.01587,-0.04553 0.384887,-1.129882 0.0089,-2.453369
            z" />
      <path d="m 16.640287,12.848166 v -2.285523 h 1.433472
            c 0.145264,0.754028 0.536377,1.613037 0.97876,2.285523 z" />
      <path d="M 19.014921,10.04299 C 19.004301,9.7746792 18.78372,9.562643 18.515287,9.562643 h -2.375
            c -0.276123,0 -0.5,0.223877 -0.5,0.5 v 3.285523 c 0,0.276123 0.223877,0.5 0.5,0.5 h 3.967408
            c 0.207885,0 0.394165,-0.128663 0.467651,-0.323121 0.07361,-0.19458 0.01904,-0.414184 -0.136841,-0.551757
            C 19.846708,12.451071 19.050077,10.9363 19.014921,10.04299 Z" />
      <path d="m 5.6305375,17.388113
            c -0.073242,-0.440064 -0.3642578,-0.702759 -0.7780762,-0.702759 -0.4272461,0 -0.7492676,0.337525 -0.7492676,0.785034 0,0.44751 0.3220215,0.785035 0.7492676,0.785035 0.2341309,0 0.4633789,-0.105835 0.6137695,-0.283204 0.138916,-0.164062 0.1987305,-0.376953 0.1643066,-0.583984
            z" />
      <path d="M 18.609375 16.809896 C 18.202057 16.830117 17.901042 17.160225 17.901042 17.59375
            C 17.901042 18.04126 18.223796 18.380208 18.651042 18.380208
            C 18.885173 18.380208 19.11263 18.273722 19.263021 18.096354
            C 19.401937 17.932292 19.461507 17.720052 19.427083 17.513021
            C 19.354083 17.072835 19.065349 16.809896 18.651042 16.809896
            C 18.63769 16.809896 18.622514 16.809244 18.609375 16.809896 z" />
    </g>
    <g transform="translate(3 3) scale(0.75)" fill="#2B2B2B">
      <path d="M23.4327679,15.1480923 c-0.2730713-0.9619141,0.0054932-2.317749,0.0083008-2.3312988
            c0.0300293-0.1430664-0.0040283-0.2921143-0.0931396-0.4078369
            s-0.2243652-0.1870117-0.3702393-0.1945801
            c-1.0202637-0.0527344-2.3162842-2.583252-2.9107666-4.3631592
            c-0.0751953-0.225708-0.3006592-0.3665771-0.5350342-0.3381348
            c-0.0142822,0.0018311-1.4415283,0.1745605-2.8916016,0.1745605
            c-1.0031738,0-1.7507019-0.0827484-2.1105957-0.1326294 V4.3059292
            c0-0.2730713-0.2191162-0.4957278-0.4923096-0.4998782
            C13.7241497,3.8012903,6.338408,3.6899621,4.5696092,3.9426477
            C3.3885787,4.111227,0.9855025,3.8127649,0.9613326,3.8098352
            C0.8164351,3.792135,0.6692183,3.8382776,0.5608199,3.9375207
            C0.4526656,4.0368862,0.3935835,4.178854,0.3993208,4.3257046
            C0.6212447,9.9450893,0.72769,16.2690639,0.4962447,17.1402798
            c-0.0817871,0.1112061-0.1162109,0.2542725-0.0871582,0.3963623
            c0.0454102,0.2226562,0.2352295,0.3865967,0.4619141,0.399292
            c0,0,0.5872803,0.0327148,1.3905182,0.0643158
            c0.2142029,1.3152313,1.158783,2.1909332,2.4429781,2.1909332
            c0.8419189,0,1.697876-0.3930664,2.2338867-1.0258789
            c0.3227081-0.3809204,0.5086365-0.8219757,0.5504761-1.2787781
            c2.0894465-0.0811462,6.4786983,0.0484161,6.5258174,0.0498962
            c0.0343781,0.0010071,0.068634-0.0018921,0.1022949-0.0079346
            c0.026535,0.0043488,0.0532837,0.0081787,0.0810547,0.0081787 h1.704422
            c0.2347565,1.0518188,1.1827545,1.8868713,2.5934296,2.239624
            c0.1235352,0.0308838,0.248291,0.0461426,0.3734131,0.0461426
            c0.3886719,0,0.7811279-0.1480713,1.1523438-0.4378662
            c0.5736847-0.4479828,1.0622559-1.2360382,1.2663116-1.8479004 h1.6638641
            c0.2126465,0,0.4019775-0.1343994,0.4720459-0.3352051
            C23.4397259,17.5559292,23.8087444,16.4715786,23.4327679,15.1480923 z M6.1754441,18.5189419
            c-0.3513184,0.4146729-0.914917,0.6722412-1.4709473,0.6722412
            c-1.0991213,0-1.489136-0.9552002-1.489136-1.7731934 s0.3900146-1.7733154,1.489136-1.7733154
            c0.9315186,0,1.5213623,0.3035889,1.7800293,1.8554688 c0,0,0,0,0,0.0001221
            C6.5597215,17.9518032,6.3579392,18.3033657,6.1754441,18.5189419 z M13.5296917,16.9226284
            c-1.2529907-0.0328217-4.4511108-0.1040649-6.1511989-0.0321655
            c-0.3701324-1.5275574-1.227417-2.2457886-2.673996-2.2457886
            c-1.3360598,0-2.3020175,0.9493103-2.4627993,2.3539734
            c-0.267868-0.0106659-0.5112915-0.0214081-0.7121277-0.0308533
            c0.1002197-0.8557129,0.1306152-2.5859375,0.0599365-6.2004395
            c-0.0469971-2.4093018-0.1298828-4.824707-0.1691895-5.9008789
            c0.8306885,0.0826416,2.3522949,0.1998291,3.2906497,0.0661621
            c1.4381104-0.2054443,7.1063228-0.1538086,8.8187256-0.1333008 V16.9226284 z
            M19.3933392,19.0062466 c-0.3581543,0.2740479-0.581665,0.2183838-0.6549072,0.1998291
            c-0.444458-0.111084-1.8945312-0.5683594-1.8945312-1.7880859
            c0-0.970459,1.1739502-1.7733154,1.7731934-1.7733154
            c0.9776611,0,1.7729492,0.7954102,1.7731934,1.7730713
            C20.3858929,17.6334438,19.988554,18.5506802,19.3933392,19.0062466 z M22.5553265,16.9366665
            h-1.2097168 c-0.2288818-1.3001556-1.3637695-2.2919922-2.7285156-2.2919922
            c-0.9767761,0-2.4241333,0.9561462-2.7168579,2.2919922 h-1.2022095 V8.5843716
            c0.4400635,0.0498047,1.1070557,0.1032715,1.9422607,0.1032715
            c1.0545654,0,2.0732422-0.0847168,2.614502-0.1386719
            c0.1662598,0.4454346,0.4768066,1.2208252,0.8914795,2.0098877
            c0.7275391,1.3840332,1.4779053,2.2327881,2.2376709,2.5332031
            c-0.0738525,0.5645752-0.1418457,1.5235596,0.0869141,2.3292236
            C22.6474895,16.0431118,22.6102581,16.5999966,22.5553265,16.9366665 z" />
      <path d="M19.0149212,10.0429897 c-0.0106201-0.2683105-0.2312012-0.4803467-0.4996338-0.4803467 h-2.375
            c-0.276123,0-0.5,0.223877-0.5,0.5 v3.2855225 c0,0.276123,0.223877,0.5,0.5,0.5 h3.9674072
            c0.2078857,0,0.394165-0.1286621,0.4676514-0.3231201
            c0.0736084-0.1945801,0.019043-0.4141846-0.1368408-0.5517578
            C19.8467083,12.4510708,19.0500774,10.9363003,19.0149212,10.0429897 z M16.6402874,12.8481655
            v-2.2855225 h1.4334717 c0.1452637,0.7540283,0.536377,1.6130371,0.9787598,2.2855225
            H16.6402874 z" />
      <path d="M5.6305375,17.388113 c-0.0732422-0.4400635-0.3642578-0.7027588-0.7780762-0.7027588
            c-0.4272461,0-0.7492676,0.3375244-0.7492676,0.7850342
            s0.3220215,0.7850342,0.7492676,0.7850342 c0.2341309,0,0.4633789-0.105835,0.6137695-0.2832031
            c0.138916-0.1640625,0.1987305-0.3769531,0.1643066-0.5839844
            C5.6305375,17.388113,5.6305375,17.388113,5.6305375,17.388113 z" />
      <path d="M18.6500683,16.809988 c-0.4272461,0-0.7492676,0.3375244-0.7492676,0.7850342
            s0.3220215,0.7850342,0.7492676,0.7850342 c0.2341309,0,0.4633789-0.105835,0.6137695-0.2832031
            c0.138916-0.1640625,0.1987305-0.3769531,0.1643066-0.5839844
            C19.3551464,17.0726833,19.0643749,16.809988,18.6500683,16.809988 z" />
    </g>
  </svg>
)


/** Shared stroke for the fine-line set; the drawings are authored on a 64-unit grid. */
const line = {
  viewBox: '6 6 52 52',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.25,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

/** Fine-line step 1 (choose your styles): a t-shirt on a hanger. */
export const LineSelectStyles = (p: IconProps) => (
  <svg {...line} {...p}>
    <path d="M29 12.5a3 3 0 1 1 6 0c0 2.4-3 3-3 5.2V20" />
    <path d="M24.8 22.8 32 20l7.2 2.8" />
    <path d="M24.8 22.8c3.2 2.2 11.2 2.2 14.4 0" />
    <path d="M24.8 22.8c1.7 6.2 12.7 6.2 14.4 0" />
    <path d="M24.8 22.8 18.4 25.2Q17.1 25.7 16.2 27.2L11.2 35.4 19.5 40.7" />
    <path d="M39.2 22.8 45.6 25.2Q46.9 25.7 47.8 27.2L52.8 35.4 44.5 40.7" />
    <path d="M19.5 34V56h25V34" />
  </svg>
)

/** Fine-line step 2 (pack your styles): a parcel tied with a bow. */
export const LinePackStyles = (p: IconProps) => (
  <svg {...line} {...p}>
    <rect x="13" y="22" width="38" height="31" rx="2.5" />
    <path d="M32 22v10M32 42v11M13 37.5h8.5M42.5 37.5H51" />
    <path d="M32 37.5c-3.8-5.4-10-6.6-10.6-3-.6 3.4 5.4 4 10.6 3Z" />
    <path d="M32 37.5c3.8-5.4 10-6.6 10.6-3 .6 3.4-5.4 4-10.6 3Z" />
    <path d="m32 37.5-4.5 8M32 37.5l4.5 8" />
  </svg>
)

/** Fine-line step 3 (drop off at the parcel shop): a location pin with a heart. */
export const LineShipPackage = (p: IconProps) => (
  <svg {...line} {...p}>
    <path d="M32 54s-15-13.2-15-25a15 15 0 0 1 30 0c0 11.8-15 25-15 25Z" />
    <path d="M32 34.5c-4.4-2.7-6.5-5.1-6.5-7.4a3.25 3.25 0 0 1 6.5-.6 3.25 3.25 0 0 1 6.5.6c0 2.3-2.1 4.7-6.5 7.4Z" />
    <path d="M24 57.5h16" />
  </svg>
)

/**
 * Shared frame for v1's set: drawn on a 45-unit grid, filled in `currentColor`. The viewBox is cropped
 * to the three drawings' common bounds so they fill the badge box while keeping v1's relative sizes.
 */
const classic = { viewBox: '2.5 2.5 40 40', fill: 'currentColor' }

/** Classic step 1 (trade it in): a hand picking a tile. */
export const ClassicSelectStyles = (p: IconProps) => (
  <svg {...classic} {...p}>
    <g transform="translate(-38 -817)">
      <g transform="translate(43.5 819.5)">
        <path transform="translate(166.76 -22.777)" d="M-140.2,51.077a4.109,4.109,0,0,0-.369-.541,2.027,2.027,0,0,0-.866-.591,2.907,2.907,0,0,0-2.141.222,45.265,45.265,0,0,1-4.592,1.939l-.033.013-.033-.007c-.465-.095-.779-.464-.986-1.155a4.526,4.526,0,0,1,.054-2.582l5.355-13.123a6.5,6.5,0,0,0,.386-1.654,1.906,1.906,0,0,0-.093-.814,1.273,1.273,0,0,0-.661-.7l-1.626-.671a1.371,1.371,0,0,0-.868.06,1.691,1.691,0,0,0-.6.476,7.236,7.236,0,0,0-.885,1.427c-.916,1.769-2.043,4.147-3.038,6.245l-.114.24c-1.078,2.277-1.75,3.651-2.117,4.326l-.037.068-7-.356a5.1,5.1,0,0,0-1.708.193,2.388,2.388,0,0,0-1.711,1.428l0,.006A61.058,61.058,0,0,0-166.7,55.773a4.561,4.561,0,0,0,.066,1.85,2.034,2.034,0,0,0,1.123,1.5l8.323,3.429a3.624,3.624,0,0,0,.639.182,2.925,2.925,0,0,0,.512.045A3.068,3.068,0,0,0-155,62.6c.086-.033.169-.071.294-.129l.089-.04c3.036-1.318,7.307-3.985,12.691-7.92a9.678,9.678,0,0,0,1.428-1.235A2.252,2.252,0,0,0-140.2,51.077Zm-5.284-16.453-5.355,13.126a6.045,6.045,0,0,0-.1,3.7,3.254,3.254,0,0,0,2.689,2.5,1.006,1.006,0,0,0,.357-.032,36.321,36.321,0,0,0,5.023-2.1,1.433,1.433,0,0,1,.84-.179.4.4,0,0,1,.2.2c.062.167.062.167-.054.318a10.169,10.169,0,0,1-1.084.915,73.293,73.293,0,0,1-12.429,7.75,1.681,1.681,0,0,1-.846.149l-.165-.034c-1.882-.768-3.9-1.6-5.889-2.425l-2.472-1.022a1.206,1.206,0,0,1-.117-.288,2.955,2.955,0,0,1-.041-1.129,59.33,59.33,0,0,1,2.74-9.965c.013-.04.135-.174.5-.286a3.9,3.9,0,0,1,1.124-.126l7.472.382a1.109,1.109,0,0,0,.978-.532c.453-.77,1.265-2.485,2.205-4.472l.208-.44c1.034-2.183,2.214-4.66,3.123-6.418.162-.312.309-.58.442-.8a1.8,1.8,0,0,1,.085-.159l.831.345A5.773,5.773,0,0,1-145.485,34.625Z" />
        <g transform="translate(5.454 2.75)">
          <path transform="translate(161.25 -25.509)" d="M-160.508,27.992h-.016a.738.738,0,0,1-.727-.739,1.691,1.691,0,0,1,1.732-1.745.745.745,0,0,1,.742.748.745.745,0,0,1-.742.748c-.111,0-.237,0-.247.263A.753.753,0,0,1-160.508,27.992Z" />
        </g>
        <path transform="translate(166.567 -22.75)" d="M-134.04,22.75h-12.17a1.48,1.48,0,0,0-1.474,1.483v6.828l.233-.164a2.855,2.855,0,0,1,.254-.172,2.379,2.379,0,0,1,.3-.185h0l0,0a2.576,2.576,0,0,1,.521-.147l.17-.034-.011-6.11,12.158-.012.012,12.245h-9.021l-.435,1.066-.175.43h9.63a1.48,1.48,0,0,0,1.473-1.483V24.233A1.48,1.48,0,0,0-134.04,22.75Z" />
        <path transform="translate(166.73 -22.75)" d="M-162.277,37.973H-151.5c.238-.5.475-1,.715-1.5l-11.484.012-.012-12.245,12.158-.012V35.1l.309-.62c.144-.29.278-.556.417-.83s.265-.531.392-.778c.058-.125.135-.25.206-.377.053-.1.1-.2.161-.3V24.233a1.479,1.479,0,0,0-1.473-1.483h-12.17a1.479,1.479,0,0,0-1.473,1.483V36.49A1.479,1.479,0,0,0-162.277,37.973Z" />
      </g>
    </g>
  </svg>
)

/** Classic step 2 (pack it): an open box with an arrow going in. */
export const ClassicPackStyles = (p: IconProps) => (
  <svg {...classic} {...p}>
    <g transform="translate(5411 -4226)">
      <g transform="translate(-1.536 115.857)">
        <g transform="translate(-5406.964 4114.143)">
          <g transform="translate(0 4.282)">
            <path transform="translate(127.683 -29.438)" d="M-87.76,42.146l-3.133-6.564a.976.976,0,0,0-.454-.415l-9.818-3.467c.016.028.038.051.054.08a2.728,2.728,0,0,1,.284,1.668l6.961,2.477-13.819,4.859L-121.5,35.87l9.526-3.35a2.708,2.708,0,0,1-.142-1.55L-124,35.182a.76.76,0,0,0-.416.4l-3.191,6.582a.9.9,0,0,0-.026.632.673.673,0,0,0,.428.39l2.687.935V55.742a.7.7,0,0,0,.5.685l16.1,5.7a.745.745,0,0,0,.21.023.7.7,0,0,0,.265-.024l16.084-5.7a.791.791,0,0,0,.517-.712V44.175l2.67-.93a.69.69,0,0,0,.451-.414A1.025,1.025,0,0,0-87.76,42.146Zm-38.13-.121,2.524-5.2,14.667,5.171-2.522,5.2Zm17.448,18.3-14.566-5.19V44.684l11.947,4.2a.787.787,0,0,0,.214.024h0a.723.723,0,0,0,.673-.418l1.728-3.688Zm1.516.1V44.86l1.726,3.681a.729.729,0,0,0,.652.422.541.541,0,0,0,.242-.023l11.947-4.2V55.23Zm2.781-13.18-2.522-5.2L-92,36.871l2.523,5.206Z" />
            <path transform="translate(127.841 -29.463)" d="M-107.85,29.491l.021-.028c-.018,0-.035.005-.053.006A.365.365,0,0,1-107.85,29.491Z" />
          </g>
          <path transform="translate(127.82 -25.25)" d="M-97.031,25.25a12.3,12.3,0,0,0-10.646,7.823l-1.173-1.947a.893.893,0,0,0-1.235-.3.911.911,0,0,0-.3,1.246l2.625,4.361,4.562-2.467a.909.909,0,0,0,.367-1.227.894.894,0,0,0-1.218-.371l-1.787.967a10.253,10.253,0,0,1,8.806-6.267.9.9,0,0,0,.9-.907A.9.9,0,0,0-97.031,25.25Z" />
          <g transform="translate(6.571 22.521)">
            <path transform="translate(121.164 -47.408)" d="M-120.408,53.854a.759.759,0,0,1-.756-.762V48.17a.759.759,0,0,1,.756-.762.759.759,0,0,1,.756.762v4.921A.759.759,0,0,1-120.408,53.854Z" />
          </g>
        </g>
      </g>
    </g>
  </svg>
)

/** Classic step 3 (drop it off): a closed box with a down arrow. */
export const ClassicShipPackage = (p: IconProps) => (
  <svg {...classic} {...p}>
    <g transform="translate(-36 -1081)">
      <g transform="translate(5403.143 -3027.639)">
        <path transform="translate(-5278.892 4088.889)" d="M-48.773,32.277a.717.717,0,0,0-.243-.443l-8.116-6.9a.772.772,0,0,0-.5-.183H-74.88a.769.769,0,0,0-.538.217l-7.1,6.9a1.882,1.882,0,0,0-.481.786,1.847,1.847,0,0,0-.25.913V57.834a1.947,1.947,0,0,0,1.971,1.916h31.058a1.947,1.947,0,0,0,1.971-1.916V33.567A1.876,1.876,0,0,0-48.773,32.277Zm-2.77-.626H-64.989V26.229h7.069Zm-23.022-5.423h8.054v5.423H-80.145Zm24.793,31.606a.444.444,0,0,1-.449.437H-81.279a.444.444,0,0,1-.449-.437V33.567a.444.444,0,0,1,.449-.437h31.058a.444.444,0,0,1,.449.437Z" />
        <path transform="translate(-5278.703 4088.685)" d="M-61.81,47.4a.776.776,0,0,0-1.072-.08l-2.295,1.911V39.989a.751.751,0,0,0-.761-.739.751.751,0,0,0-.761.739v9.237l-2.295-1.911a.776.776,0,0,0-1.072.08.724.724,0,0,0,.082,1.042l4.046,3.371,4.046-3.371A.724.724,0,0,0-61.81,47.4Z" />
        <path transform="translate(-5278.497 4088.738)" d="M-56,36.244v-.016a.743.743,0,0,1,.752-.724,1.7,1.7,0,0,1,1.775,1.725.751.751,0,0,1-.761.739.751.751,0,0,1-.761-.739c0-.11,0-.236-.268-.246A.758.758,0,0,1-56,36.244Z" />
      </g>
    </g>
  </svg>
)

/** The bundled artwork in step order (select styles → pack styles → ship package), per style. */
const STEP_ART = {
  [StepIconStyle.Badge]: [ArtSelectStyles, ArtPackStyles, ArtShipPackage],
  [StepIconStyle.Line]: [LineSelectStyles, LinePackStyles, LineShipPackage],
  [StepIconStyle.Classic]: [ClassicSelectStyles, ClassicPackStyles, ClassicShipPackage],
}

/**
 * One "how it works" step badge, shared by the login page's steps section and the intro dialog so the
 * two never drift. Renders the bundled artwork for `index` in the configured `stepIcons.style`
 * (badges by default, the fine-line set, or v1's classic set), or the brand's own image when
 * `tradeInConfig.stepIcons.images[index]` supplies one (a missing/empty entry keeps the default).
 *
 * Both cases suppress the CSS badge disc — the artwork draws its own, and a brand image brings its own
 * shape — via the `step-art` / `step-img` modifiers; `className` is the host's base badge class.
 *
 * `fit` means "the badge box is fixed, scale into it": the dialog puts the badge in a grid column
 * beside the text, where a brand image at `stepIcons.width` (or even its 72px default) would spill out
 * of the column and overlap the copy. That config sizes the login page's stacked layout, so it's
 * applied there and ignored here — CSS alone can't do it, since an inline width beats a stylesheet.
 */
export function StepArt({
  index,
  className,
  fit = false,
}: {
  index: number
  className: string
  fit?: boolean
}) {
  const stepIcons = getStepIcons()
  const custom = stepIcons?.images?.[index]?.trim()
  const set = STEP_ART[getStepIconStyle()]
  const Art = set[index] ?? set[0]
  return (
    <span className={`${className} ${custom ? 'step-img' : 'step-art'}`}>
      {custom ? (
        <img
          src={custom}
          alt=""
          style={!fit && stepIcons?.width ? { width: cssLength(stepIcons.width) } : undefined}
        />
      ) : (
        <Art />
      )}
    </span>
  )
}
